import { getDifferenceInHours } from '../../../utils/timeRelatedFunctions';
import {
  baseMarks,
  initialMarks,
  marksStartingFromZero,
  markType,
  ticksToShow,
  ticksToShowFromZero,
} from '../constants/basemarks';

// 時刻の計算はすべて epoch milliseconds で行い、表示用のローカル時刻とは分ける.
export const HOUR_MS = 60 * 60 * 1000;
// バックエンド（/map/）が返すバンドの上限. Asset は起算時刻から 0〜38 時間後の 39 バンド.
export const MAX_BAND = 38;

export type Candidate = { imageId: string; originMs: number };

// 1枚の地図（ある Asset のあるバンド）. key は URL のキャッシュに使う.
export type FrameRef = {
  key: string;
  imageId: string;
  band: number;
  validTimeMs: number;
};

export const frameKey = (imageId: string, band: number) => `${imageId}:${band}`;

// "te_flddph_YYYYMMDDHH" の末尾は UTC の起算時刻.
export const parseAssetOriginMs = (imageId: string): number | null => {
  const match = /(\d{4})(\d{2})(\d{2})(\d{2})$/.exec(imageId);
  if (!match) {
    return null;
  }
  const [, year, month, day, hour] = match.map(Number);
  return Date.UTC(year, month - 1, day, hour);
};

export const generateTimeList = (increment: number, delta: number) =>
  Array.from({ length: MAX_BAND + 1 }, (_, i) => i).filter(n => n % increment === delta);

// imageIdList 全て取る必要はなく, 最新のものとそこから27h以内で一番古いものだけで36h分の時刻を埋められる.
// 起算時刻の差は increment の倍数に限る（同じ時刻の目盛りに乗るようにするため）. 新しい順に返す.
export const selectCandidates = (imageIdList: string[], increment: number): Candidate[] => {
  const parsed = imageIdList
    .map(imageId => ({ imageId, originMs: parseAssetOriginMs(imageId) }))
    .filter((c): c is Candidate => c.originMs !== null)
    .sort((a, b) => b.originMs - a.originMs);
  if (parsed.length < 1) {
    return [];
  }

  const latest = parsed[0];
  const thresholdMs = latest.originMs - 27 * HOUR_MS;
  let oldest = latest;
  for (const candidate of parsed) {
    const diff = latest.originMs - candidate.originMs;
    if (candidate.originMs >= thresholdMs && diff % (increment * HOUR_MS) === 0) {
      oldest = candidate;
    }
  }
  return oldest === latest ? [latest] : [latest, oldest];
};

// 使えない（取得に失敗した）Asset・バンドかどうか. key は frameKey.
export type FrameSkip = (key: string) => boolean;

// 有効時刻を Asset・バンドに対応付ける. 同じ時刻を持つ Asset が複数あれば新しい起算を優先する.
// skip に当たるものは古い起算で代わりに表示する（最新の Asset が GEE への登録前で取れない間など）.
// 代わりが無ければ、skip に当たっても最も新しいものを返す（失敗として見せるため）.
export const resolveFrame = (
  candidates: Candidate[],
  bands: number[],
  validTimeMs: number,
  skip?: FrameSkip,
): FrameRef | null => {
  let newest: FrameRef | null = null;
  for (const candidate of candidates) {
    const band = (validTimeMs - candidate.originMs) / HOUR_MS;
    if (!Number.isInteger(band) || !bands.includes(band)) {
      continue;
    }
    const frame = { key: frameKey(candidate.imageId, band), imageId: candidate.imageId, band, validTimeMs };
    if (!skip || !skip(frame.key)) {
      return frame;
    }
    newest = newest ?? frame;
  }
  return newest;
};

// [minMs, maxMs] に入る全ての時刻を、表示に使う Asset・バンドで列挙する（時刻の昇順）.
export const listFrames = (
  candidates: Candidate[],
  bands: number[],
  minMs: number,
  maxMs: number,
  skip?: FrameSkip,
): FrameRef[] => {
  const validTimes = new Set<number>();
  for (const candidate of candidates) {
    for (const band of bands) {
      const validTimeMs = candidate.originMs + band * HOUR_MS;
      if (minMs <= validTimeMs && validTimeMs <= maxMs) {
        validTimes.add(validTimeMs);
      }
    }
  }
  return Array.from(validTimes)
    .sort((a, b) => a - b)
    .map(validTimeMs => resolveFrame(candidates, bands, validTimeMs, skip))
    .filter((frame): frame is FrameRef => frame !== null);
};

// 先読みの順番. 再生は未来へ進むので、選択中より後ろを近い順に、その後で前を近い順に並べる.
export const orderForPrefetch = (frames: FrameRef[], selectedMs: number): FrameRef[] => {
  const forward = frames.filter(f => f.validTimeMs > selectedMs).sort((a, b) => a.validTimeMs - b.validTimeMs);
  const backward = frames.filter(f => f.validTimeMs < selectedMs).sort((a, b) => b.validTimeMs - a.validTimeMs);
  return [...forward, ...backward];
};

// 先頭から同じ Asset のバンドを最大 size 本ずつまとめる. 1回の /map/ で取る単位になる.
export const takeChunk = (frames: FrameRef[], size: number): { imageId: string; bands: number[] } | null => {
  if (frames.length < 1) {
    return null;
  }
  const imageId = frames[0].imageId;
  const bands = frames.filter(f => f.imageId === imageId).slice(0, size).map(f => f.band);
  return { imageId, bands };
};

export type SliderSetup = {
  calculatedTimeMs: number;
  initialValue: number;
  marks: markType;
};

// 起算時刻が決まったときのスライダーの初期値と目盛り.
// 起算時刻から見て (-12h, +36h) に現在時刻があれば現在時刻を、それ以外は起算時刻を初期値にする.
export const computeSliderSetup = (calculatedTime: Date, now: Date): SliderSetup => {
  const calculatedTimeMs = calculatedTime.getTime();
  const timeDelta = Math.floor(getDifferenceInHours(calculatedTime, now)); // now - calc
  if (timeDelta < 36 && timeDelta > -12) {
    const newMarks = baseMarks
      .map(bm => ({ ...bm, label: String(Number(bm.label) - timeDelta) }))
      .filter(m => ticksToShow.includes(Number(m.label)));
    newMarks.push({ value: timeDelta, label: '0' });
    const marks = newMarks.map(m => ({
      value: m.value,
      label: (!!Number(m.label) && `${m.label}h`) || '現在',
    }));
    return { calculatedTimeMs, initialValue: timeDelta, marks };
  }
  const marks = marksStartingFromZero.filter(m => ticksToShowFromZero.includes(Number(m.value)));
  return { calculatedTimeMs, initialValue: 0, marks };
};

export const defaultSliderMarks = initialMarks;

export const findSliderMaxMin = (marks: markType) => {
  const list = marks.map(m => m.value);
  return { sliderMax: Math.max(...list), sliderMin: Math.min(...list) };
};
